import { useApolloClient } from '@apollo/client';
import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Plus } from '@styled-icons/bootstrap/Plus';
import { useLogEvent } from 'hooks/useLogEvent';
import useUploadState from 'FeatureModules/Document/hooks/useUploadState';
import { DialogTitle } from 'DesignSystem';
import filter from 'lodash/filter';
import uniqBy from 'lodash/uniqBy';
import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { IBaseDocument } from 'FeatureModules/Document/types/document.types';
import EditDocumentContainer from './Dialogs/EditDocumentContainer';
import SelectDocumentsDialog from 'FeatureModules/Document/Components/Dialogs/SelectDocumentsDialog';
import DocumentUploadItem from './DocumentUploadItem';
import SubTitleUploadItem from './SubTitleUploadItem';
import useDocumentUploadActions from 'FeatureModules/Document/hooks/useDocumentUploadActions';
import { DOCUMENT_FRAGMENT } from 'graphql/secure/fragments/document.fragments';
import Dropzone from 'Components/FileUpload/Dropzone';
import UploadListItem from 'Components/FileUpload/UploadListItem';

const SelectDocumentsButton = styled(Button)(() => ({
  position: 'absolute',
  right: 0,
  top: 0,
}));

interface IDocumentUploadProps {
  documents: IBaseDocument[];
  deletedDocuments: string[];
  title: string;
  displaySelector?: boolean;
  multipleDocuments?: boolean;
  isRequestDocument?: boolean;

  /** Pre selects the site id and disables the site selection */
  defaultSiteId?: string;
  /** Pre selects the owned by id and disables the owned by selection */
  defaultOwnedById?: string;

  handleDelete: (id: string) => void;
  handleDocumentsChange: (documents: IBaseDocument[]) => void;
}

const DocumentUpload: FC<IDocumentUploadProps> = ({
  documents,
  handleDocumentsChange,
  deletedDocuments,
  handleDelete,
  title,
  displaySelector,
  defaultSiteId,
  defaultOwnedById,
  multipleDocuments = true,
  isRequestDocument = false,
}) => {
  const { fileIDs, fileMap, createdRecords } = useUploadState();
  const { getSelectedDocuments, onDrop, maxSize } = useDocumentUploadActions();
  const client = useApolloClient();
  const [editableDocument, setEditableDocument] = useState<IBaseDocument>();
  const [openSelector, setOpenSelector] = useState<boolean>(false);
  const { logEvent } = useLogEvent();
  const handleEdit = (id: string) => {
    const document = documents.find(document => document.id === id);
    setEditableDocument(document);
  };

  const displayedFileIds = useMemo(() => {
    return filter(fileIDs, id => {
      const { recordId } = fileMap[id];
      return !deletedDocuments.includes(recordId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileIDs, deletedDocuments]);

  const displayedExistingDocuments = useMemo(() => {
    return documents.filter(({ id }) => {
      return (
        !createdRecords.find(createdRecord => createdRecord.id === id) &&
        !deletedDocuments.includes(id)
      );
    });
  }, [documents, createdRecords, deletedDocuments]);

  useEffect(() => {
    const allDocuments = uniqBy([...createdRecords, ...documents], 'id')
      .map(({ id }) =>
        client.readFragment({
          fragment: DOCUMENT_FRAGMENT,
          id: `Document:${id}`,
          fragmentName: 'documentValues',
        })
      )
      .filter(doc => doc);

    if (!allDocuments?.length) return;

    const filteredDocuments = filter(allDocuments, ({ id }) => !deletedDocuments.includes(id));

    handleDocumentsChange(filteredDocuments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedFileIds, createdRecords, editableDocument]);

  const handleSelectDocuments = (selectedIds: string[]) => {
    const markedDocuments = getSelectedDocuments(selectedIds);
    handleDocumentsChange(markedDocuments);
  };

  return (
    <Box position="relative">
      <Box mb={2}>
        <DialogTitle>{title}</DialogTitle>
      </Box>

      <Box overflow="auto">
        {displaySelector && (
          <SelectDocumentsButton
            onClick={() => {
              logEvent('OPEN_SELECT_DOCUMENT');
              setOpenSelector(true);
            }}
            color="inherit"
            variant="contained"
            size="small"
            startIcon={<Plus size={14} />}
            data-testid="document-selector-button"
            disabled={!multipleDocuments && documents.length >= 1}
          >
            Select from document list
          </SelectDocumentsButton>
        )}

        <Dropzone
          onDrop={onDrop}
          supportedFileTypes={['pdf', 'image']}
          maxFileMb={maxSize}
          multiple={multipleDocuments}
          disabled={!multipleDocuments && documents.length >= 1}
        />

        {displayedFileIds.map(fileID => {
          const { recordId } = fileMap[fileID];
          return (
            <>
              {editableDocument && editableDocument?.id === recordId ? (
                <Box mb={1} key={fileID}>
                  <EditDocumentContainer
                    isRequestDocument={isRequestDocument}
                    documentId={recordId}
                    defaultSiteId={defaultSiteId}
                    defaultOwnedById={defaultOwnedById}
                    hideCancelButton
                    onDelete={() => {
                      handleDelete(editableDocument.id);
                      setEditableDocument(undefined);
                    }}
                    onClose={() => {
                      setEditableDocument(undefined);
                      setOpenSelector(false);
                    }}
                  />
                </Box>
              ) : (
                <DocumentUploadItem
                  key={fileID}
                  id={fileID}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  openOnSuccess
                  editableDocument={editableDocument}
                />
              )}
            </>
          );
        })}

        {displayedExistingDocuments.map(({ title, id, expiryDate, category }) => (
          <>
            {editableDocument && editableDocument?.id === id ? (
              <Box mb={1} key={id}>
                <EditDocumentContainer
                  isRequestDocument={isRequestDocument}
                  onClose={() => setEditableDocument(undefined)}
                  documentId={id}
                  onDelete={() => {
                    handleDelete(editableDocument.id);
                    setEditableDocument(undefined);
                  }}
                />
              </Box>
            ) : (
              <UploadListItem
                key={id}
                fileName={title}
                success
                onDelete={() => handleDelete(id)}
                onEdit={() => handleEdit(id)}
                onOpen={undefined}
                subTitle={<SubTitleUploadItem expiryDate={expiryDate} category={category} />}
              />
            )}
          </>
        ))}

        {openSelector && (
          <SelectDocumentsDialog
            open={openSelector}
            onClose={() => setOpenSelector(false)}
            selectedDocumentIds={documents?.map(doc => doc.id)}
            onSelect={handleSelectDocuments}
            multiselect={multipleDocuments}
          />
        )}
      </Box>
    </Box>
  );
};

export default DocumentUpload;
